import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Play, TrendingUp, Users, Sparkles } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { ScrollReveal } from './ScrollReveal';
import { SectionCTA } from './SectionCTA';
const days = [{
  day: 'Día 2',
  icon: <TrendingUp className="w-5 h-5" data-id="element-89" />,
  quote: '"La mayoría no vende porque intenta vender antes de estructurar su oferta. Te muestro el error."',
  tag: 'Estructura de oferta',
  color: 'purple',
  gradient: 'from-purple-500 to-violet-600',
  bg: 'bg-purple-500/10',
  border: 'border-purple-500/20',
  text: 'text-purple-400'
}, {
  day: 'Día 3',
  icon: <Users className="w-5 h-5" data-id="element-90" />,
  quote: '"Si tienes 300 seguidores puedes vender. El problema no es tu audiencia, es tu propuesta."',
  tag: 'Propuesta de valor',
  color: 'emerald',
  gradient: 'from-emerald-500 to-green-600',
  bg: 'bg-emerald-500/10',
  border: 'border-emerald-500/20',
  text: 'text-emerald-400'
}];
function VideoCard({
  item,
  index
}: {
  item: (typeof days)[0];
  index: number;
}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.15,
    ease: 'easeOut'
  }} onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)} className="group relative cursor-default" data-id="element-91">
      <div className={`relative rounded-2xl bg-[#0c0c20] border ${item.border} overflow-hidden transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,0,0,0.4)]`} data-id="element-92">
        {/* Fake video thumbnail area */}
        <div className="relative aspect-video bg-gradient-to-br from-[#0a0a1a] to-[#12122a] overflow-hidden" data-id="element-93">
          {/* Scanline overlay */}
          <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,0,0,0.15)_2px,rgba(0,0,0,0.15)_4px)] pointer-events-none z-10" data-id="element-94" />

          {/* Gradient background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-10`} data-id="element-95" />

          {/* Floating orb */}
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-br ${item.gradient} opacity-20 blur-[40px]`} data-id="element-96" />

          {/* Day label */}
          <div className={`absolute top-4 left-4 z-20 px-3 py-1 rounded-full ${item.bg} border ${item.border} backdrop-blur-sm`} data-id="element-97">
            <span className={`text-xs font-bold uppercase tracking-widest ${item.text}`} data-id="element-98">
              {item.day}
            </span>
          </div>

          {/* Play button */}
          <motion.div animate={hovered ? {
          scale: 1.1
        } : {
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="absolute inset-0 flex items-center justify-center z-20" data-id="element-99">
            <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.5)] group-hover:shadow-[0_0_40px_rgba(0,212,255,0.3)] transition-shadow duration-500`} data-id="element-100">
              <Play className="w-6 h-6 text-white fill-white ml-1" data-id="element-101" />
            </div>
          </motion.div>

          {/* Waveform decoration */}
          <div className="absolute bottom-4 left-4 right-4 z-20 flex items-end gap-0.5 h-6 opacity-40" data-id="element-102">
            {Array.from({
            length: 40
          }).map((_, i) => <div key={i} className={`flex-1 rounded-full bg-gradient-to-t ${item.gradient}`} style={{
            height: `${20 + Math.sin(i * 0.8) * 60 + Math.random() * 20}%`
          }} data-id="element-103" />)}
          </div>
        </div>

        {/* Card content */}
        <div className="p-6" data-id="element-104">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${item.bg} border ${item.border} mb-4`} data-id="element-105">
            <span className={item.text} data-id="element-106">{item.icon}</span>
            <span className={`text-xs font-semibold ${item.text}`} data-id="element-107">
              {item.tag}
            </span>
          </div>

          <p className="text-gray-300 text-sm leading-relaxed italic" data-id="element-108">
            {item.quote}
          </p>

          <div className="mt-4 flex items-center gap-2" data-id="element-109">
            <div className={`h-0.5 flex-1 bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity duration-500`} data-id="element-110" />
            <span className="text-xs text-gray-600" data-id="element-111">micro-transformación</span>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function DailyInsights() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-24 px-4 bg-[#050510] relative overflow-hidden" data-id="element-112">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-113">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-[150px]" data-id="element-114" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cyan-900/10 rounded-full blur-[120px]" data-id="element-115" />
      </div>

      <div className="container mx-auto max-w-5xl relative z-10" ref={ref} data-id="element-116">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-12" data-id="element-117">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-118">
            <Sparkles className="w-4 h-4 text-cyan-400" data-id="element-119" />
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400" data-id="element-120">
              Micro-lecciones diarias
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4" data-id="element-121">
            15 días de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-cyan-400 to-emerald-400" data-id="element-122">
              transformación real
            </span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-123">
            Cada día recibirás un video corto con una acción concreta. Sin
            relleno, sin teoría innecesaria.
          </p>
        </ScrollReveal>

        {/* Video Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-124">
          {days.map((item, index) => <VideoCard key={item.day} item={item} index={index} data-id="element-125" />)}
        </div>

        {/* More days indicator */}
        <ScrollReveal delay={0.3} className="text-center mt-8" data-id="element-126">
          <p className="text-gray-500 text-sm" data-id="element-127">
            + 13 lecciones más que te llevarán de idea a primera venta
          </p>
        </ScrollReveal>

        <SectionCTA headline="Accede a las 15 lecciones completas" buttonText="QUIERO LAS 15 LECCIONES — $16.39" data-id="element-128" />
      </div>
    </section>;
}